import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { alpha, createTheme, CssBaseline, GlobalStyles, lighten, ThemeProvider } from '@mui/material';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

export const KrThemeContext = createContext();

const baseThemeOptions = {
  typography: { fontFamily: '"Manrope"' },
  shape: { borderRadius: 16 },
  components: {
    MuiTextField: {
      defaultProps: {
        variant: 'standard',
        slotProps: { inputLabel: { shrink: true } },
      },
    },
    MuiFormControl: { defaultProps: { variant: 'standard' } },
    MuiInput: {
      styleOverrides: {
        input: ({ theme }) => ({
          '.entity-form-wrapper &.Mui-disabled': {
            WebkitTextFillColor: theme.palette.text.primary // Must override this instead of "color" because it will take precedence over "color" in MUI's implementation
          },
        }),
        underline: ({ theme }) => ({
          '&:after': {
            borderBottomColor: theme.palette.mode !== 'dark' ? theme.palette.primary.dark : theme.palette.primary.main,
          },
        })
      },
    },
    MuiInputLabel: {
      defaultProps: { shrink: true },
      styleOverrides: {
        root: ({ theme }) => ({
          '&.Mui-focused': {
            color: theme.palette.mode !== 'dark' ? theme.palette.primary.dark : theme.palette.primary.main
          }
        })
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRadius: theme.shape.borderRadius * 2,
          backgroundColor: theme.palette.surface.light
        }),
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.palette.surface.light
        })
      }
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.surface.main
        })
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.surface.light
        })
      }
    },
    MuiPickersLayout: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.shape.borderRadius,
          backgroundColor: theme.palette.surface.light
        })
      }
    },
    MuiDialogActions: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(2, 3),
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: '9999px',
          boxShadow: 'none',
          textTransform: 'none',
          padding: theme.spacing(0.5, 3),
          fontSize: '1rem',
          fontWeight: 500,
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,

          // HOVER: layer your primary.main at 90% opacity
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.dark, 0.6),
            boxShadow: 'none',
          },

          // DISABLED
          '&.Mui-disabled': {
            backgroundColor: theme.palette.action.disabledBackground,
            color: theme.palette.action.disabled,
          },
        }),
      },
    },
    MuiTabs: {
      styleOverrides: {
        // Overrides for the Tabs indicator can be specified here.
        indicator: ({ theme }) => ({
          backgroundColor: theme.palette.primary.dark, // Darker underline
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          textTransform: 'none',
          fontWeight: 500,
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            backgroundColor:
              theme.palette.mode === 'dark'
                ? theme.palette.action.hover
                : theme.palette.primary.light, // Light background on hover
          },
          '&.Mui-selected': {
            color: theme.palette.primary.dark,
            fontWeight: 600,
          },
        }),
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: ({ theme }) => ({
          // Add a light border using the divider color from the theme
          overflow: 'hidden', // ensure inner buttons don't leak corners
          borderRadius: theme.shape.borderRadius, // round the whole group
          border: `1px solid ${theme.palette.divider}`, // add the border

          // Increase horizontal padding (px) to make buttons wider.
          '& .MuiToggleButton-root': {
            textTransform: 'none',
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(3),
          },
          '& .Mui-selected': {
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
          },
        }),
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        groupLabel: ({ theme }) => ({
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          fontWeight: 600,
        }),
      },
    },

  },
};

const getTheme = (primaryOptions, isDarkMode, module) => {
  const theme = createTheme({
    ...baseThemeOptions,
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
      primary: primaryOptions,
      error: {
        main: '#ef5350',
        light: '#ffd7db',
        dark: '#a73a38',
      },
      warning: {
        main: '#f6c343',
        light: '#F8E5A7',
        dark: '#b38a00',
      },
      success: {
        main: '#58c96a',
        light: '#b6e8be',
        dark: '#3a8f49',
      },
      action: {
        hover: isDarkMode
          ? 'rgba(255, 255, 255, 0.08)' // Lighter hover for dark mode
          : 'rgba(0, 0, 0, 0.08)',
      }
    },
  });

  // Special "surface" to reduce duplication across the application
  theme.palette.surface = {
    main: (isDarkMode || module == null) ? theme.palette.background.paper : theme.palette.primary.light,
    light: (isDarkMode || module == null) ? theme.palette.background.paper : lighten(theme.palette.primary.light, 0.4)
  }

  return theme;
}

const DARK_MODE_STORAGE_KEY = 'isDarkMode';

export const KrThemeProvider = ({ children }) => {

  // Vite replacement for require.context: eager loads all matching palette modules
  const modules = import.meta.glob('/src/**/*.palette.js', { eager: true });
  const modulePalettes = Object.entries(modules)
    .reduce((accumulator, [key, module]) => {
      const moduleName = key.split('/').pop().replace(/\.palette\.js$/, '');
      accumulator[moduleName] = module.default;
      return accumulator;
    }, {});

  const [isDarkMode, setIsDarkMode] = useState(JSON.parse(localStorage.getItem(DARK_MODE_STORAGE_KEY)) ?? false);
  const module = useModuleFromRoute();

  // primaryOptions come from the module's palette.js or default defined here
  const primaryOptions = modulePalettes[module] ?? {
    main: '#D1E3FF',
    light: '#F1F9FF',
    dark: '#5C84C4',
  };

  // Memoize the theme to recalc only when dark mode changes.
  const theme = useMemo(() => {
    return getTheme(primaryOptions, isDarkMode, module)
  }, [isDarkMode, module]);

  const toggleDarkMode = useCallback(() => {
    const newIsDarkMode = !isDarkMode;
    localStorage.setItem(DARK_MODE_STORAGE_KEY, JSON.stringify(newIsDarkMode));
    setIsDarkMode(newIsDarkMode);
  }, [isDarkMode]);

  const globalStyles = (
    <GlobalStyles
      styles={{
        // Font
        body: { fontFamily: theme.typography.fontFamily },

        // AG Grid Status Bar for Row Count
        '.ag-status-bar': {
          borderLeft: 'none',
          borderRight: 'none',
          borderBottom: 'none',
        },

        // Scrollbar Styles
        '*::-webkit-scrollbar': {
          width: '10px',
          height: '10px'
        },
        '*::-webkit-scrollbar-track': {
          backgroundColor: 'transparent'
        },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: isDarkMode ?'rgba(255, 255, 255, 0.25)':  'rgba(0,0,0,0.25)',
          borderRadius: '8px',
          border: '2px solid transparent',
          backgroundClip: 'content-box'
        },
        '*::-webkit-scrollbar-thumb:hover': {
          backgroundColor: isDarkMode ?'rgba(255, 255, 255, 0.25)':  'rgba(0,0,0,0.25)',
        },
      }}
    />
  );

  return (
    <KrThemeContext.Provider value={{ isDarkMode, toggleDarkMode }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {globalStyles}
        {children}
      </ThemeProvider>
    </KrThemeContext.Provider>
  );
};

export const useKrTheme = () => useContext(KrThemeContext);